import React from "react";
import { Card } from "../../components/Card";
import { Box, Button, Typography, useTheme } from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { useNavigate, useParams } from "react-router-dom";
import API_ENDPOINTS from "../../apis/endpoints";
import useSWR from "swr";
import useAuth from "../../context";

function NewsLetterSendPreview() {
    const navigate = useNavigate();
    const theme = useTheme();
    const auth = useAuth();
    const { id } = useParams();

    const { data } = useSWR(`${API_ENDPOINTS.NEWSLETTER}${id}/`);
    const newsletter = data;

    const handleDuplicate = async () => {
        try {
            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}duplicate/${id}/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": auth.deviceId,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to send");
            }

            const result = await response.json();

            const { id: new_id } = result.data;

            navigate("/dashboard/newsletter/create?edit=" + new_id);
        } catch (error) {
            console.error("Error sending newsletter:", error);
        }
    };

    return (
        <Card>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 3,
                    alignItems: "center",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <ArrowBackIosNewRoundedIcon
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate("/dashboard/newsletter/list")}
                    />
                    <Typography variant="h6">
                        {newsletter?.name ||
                            "Newsletter name lorem ipsum dolor sit amet"}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDuplicate}
                >
                    Duplicate
                </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        Sender
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {newsletter?.sender}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        {" "}
                        Recipient
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {" "}
                        {newsletter?.recipients?.join(", ")}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        {" "}
                        CC
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {" "}
                        {newsletter?.cc_recipients?.join(", ") || "-"}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        {" "}
                        BCC
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {" "}
                        {newsletter?.bcc_recipients?.join(", ") || "-"}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        {" "}
                        Subject
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {newsletter?.subject}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[300] }}
                    >
                        {" "}
                        Date & Time Stamp
                    </Typography>
                    <br />
                    <Typography
                        variant="captionRegular"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        {" "}
                        {newsletter?.sent_at
                            ? new Date(newsletter.sent_at).toLocaleString()
                            : "-"}
                    </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <div
                        dangerouslySetInnerHTML={{ __html: newsletter?.html }}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "16px",
                            marginTop: "16px",
                        }}
                    />
                </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Attachments
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {newsletter?.attachments?.map((attachment) => (
                        <Box
                            key={attachment.id}
                            sx={{
                                width: 200,
                                height: 200,
                                overflow: "hidden",
                                margin: "20px",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                            }}
                        >
                            {attachment.type.startsWith("image/") ? (
                                <img
                                    src={attachment.url}
                                    alt={attachment.name}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                    }}
                                />
                            ) : attachment.type === "application/pdf" ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        PDF Document
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={attachment.url}
                                        target="_blank"
                                    >
                                        View PDF
                                    </Button>
                                </>
                            ) : attachment.type.startsWith("audio/") ? (
                                <audio controls style={{ width: "100%" }}>
                                    <source
                                        src={attachment.url}
                                        type={attachment.type}
                                    />
                                    Your browser does not support the audio
                                    element.
                                </audio>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        File: {attachment.name}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href={attachment.url}
                                        download
                                    >
                                        Download
                                    </Button>
                                </>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Card>
    );
}

export default NewsLetterSendPreview;
